import React, { useEffect, useState } from "react";
import managedAxios, { isCancelError } from "@/utils/managedAxios";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { API_BASE } from "@/App";

const GamificationPage = () => {
  const [status, setStatus] = useState(null);
  const [expectedToday, setExpectedToday] = useState(null);
  const [mainsToday, setMainsToday] = useState(null);
  const [mcqToday, setMcqToday] = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        const [
          { data: gStatus },
          { data: expected },
          { data: mains },
          { data: mcq },
        ] = await Promise.all([
          managedAxios.get(`${API_BASE}/gamification/status`),
          managedAxios.get(`${API_BASE}/practice/expected/today`),
          managedAxios.get(`${API_BASE}/practice/mains/today`),
          managedAxios.get(`${API_BASE}/practice/mcq/today`),
        ]);
        setStatus(gStatus);
        setExpectedToday(expected);
        setMainsToday(mains);
        setMcqToday(mcq);
      } catch (e) {
        // soft fail
      }
    };
    load();
  }, []);

  return (
    <div className="max-w-4xl mx-auto px-4 py-8 space-y-4" data-testid="gamification-page">
      <div className="flex items-center justify-between gap-2">
        <div className="space-y-1">
          <h1 className="text-2xl font-semibold text-slate-900">Progress & Gamification</h1>
          <p className="text-xs text-slate-600">
            Track XP, streaks and badges. PRO learners also get rank predictor and weekend challenges.
          </p>
        </div>
        <Badge
          variant="outline"
          className="border-slate-300 text-slate-700"
          data-testid="gamification-badge"
        >
          XP Engine
        </Badge>
      </div>

      <Card className="border-slate-200 bg-white/90" data-testid="gamification-card">
        <CardContent className="p-5 text-xs text-slate-700 space-y-4">
          {status ? (
            <>
              <div className="flex flex-wrap gap-3" data-testid="gamification-main-stats">
                <div className="rounded-lg bg-slate-50 border border-slate-200 px-3 py-2">
                  <p className="text-[11px] text-slate-500">XP</p>
                  <p className="text-sm font-semibold text-slate-900">{status.xp}</p>
                </div>
                <div className="rounded-lg bg-slate-50 border border-slate-200 px-3 py-2">
                  <p className="text-[11px] text-slate-500">Level</p>
                  <p className="text-sm font-semibold text-slate-900">{status.level}</p>
                </div>
                <div className="rounded-lg bg-slate-50 border border-slate-200 px-3 py-2">
                  <p className="text-[11px] text-slate-500">Streak</p>
                  <p className="text-sm font-semibold text-slate-900">{status.streak_days} days</p>
                </div>
              </div>

              {expectedToday && (
                <div
                  className={`rounded-lg border px-3 py-2 flex items-center justify-between gap-3 ${
                    expectedToday.practiced_count >= (expectedToday.daily_goal || 3)
                      ? "border-emerald-300 bg-emerald-50/80"
                      : "border-indigo-200 bg-indigo-50/80"
                  }`}
                  data-testid="gamification-expected-strip"
                >
                  <div className="space-y-0.5">
                    <p className="text-[11px] font-semibold text-slate-900">
                      Expected Qs today
                    </p>
                    <p className="text-[11px] text-slate-800">
                      {expectedToday.practiced_count}/{expectedToday.daily_goal} practiced
                      {expectedToday.practiced_count >= expectedToday.daily_goal && " • Goal completed"}
                    </p>
                  </div>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    className="text-[11px] border-indigo-300 text-indigo-900"
                    onClick={() => (window.location.href = "/expected")}
                    data-testid="gamification-goto-expected-button"
                  >
                    Go to Expected Qs
                  </Button>
                </div>
              )}

              {(mainsToday || mcqToday) && (
                <div className="grid gap-2 md:grid-cols-2" data-testid="gamification-practice-grid">
                  {mainsToday && (
                    <div className="rounded-lg bg-slate-50 border border-slate-200 px-3 py-2">
                      <p className="text-[11px] text-slate-500">Mains answers today</p>
                      <p className="text-sm font-semibold text-slate-900">{mainsToday.practiced_count}</p>
                    </div>
                  )}
                  {mcqToday && (
                    <div className="rounded-lg bg-slate-50 border border-slate-200 px-3 py-2">
                      <p className="text-[11px] text-slate-500">MCQs attempted today</p>
                      <p className="text-sm font-semibold text-slate-900">{mcqToday.practiced_count}</p>
                    </div>
                  )}
                </div>
              )}

              {status.badges?.length > 0 && (
                <div className="space-y-1" data-testid="gamification-badges">
                  <p className="text-[11px] font-semibold text-slate-900">Badges</p>
                  <ul className="list-disc pl-4 space-y-1">
                    {status.badges.map((b, i) => (
                      <li key={i}>{b}</li>
                    ))}
                  </ul>
                </div>
              )}

              {status.rank_percentile != null && (
                <p className="text-[11px] text-slate-700" data-testid="gamification-rank">
                  Rank predictor: You are in the top {status.rank_percentile}% of active learners.
                </p>
              )}

              {status.weekend_challenge && (
                <p className="text-[11px] text-slate-700" data-testid="gamification-challenge">
                  Weekend challenge: {status.weekend_challenge}
                </p>
              )}
            </>
          ) : (
            <p className="text-[11px] text-slate-500" data-testid="gamification-loading-text">
              Loading your progress snapshot...
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default GamificationPage;
